import { descartarRascunhosPendentes } from '@/lib/equipment-registration/rascunho-pendente'
import type { AgentAction } from '@rochadenis/axiomgo-contracts'
import {
  canSendChatAction,
  captureChatSendAuthorization,
  cloneChatAction,
} from '@/lib/chat-action-permissions'
import { beginTurn, isCurrentTurn, settleCurrentTurn, turnIsSettled } from '@/lib/engine/turn-epoch'
import { confirmAttachments } from '@/lib/attachment-ack'
import { confirmComposerDraft, failComposerDraft } from '@/lib/composer-drafts'
import { create } from 'zustand'
import * as Sentry from '@sentry/react'
import toast from 'react-hot-toast'
import { ConnectionManager } from '../lib/connection-manager'
import { useChatStore } from './chatStore'
import { useImportStore } from './importStore'
import { useImportJobStore } from './importJobStore'
import { useEquipmentRegistrationFlowStore } from './equipmentRegistrationFlowStore'
import { useOnboardingFlowStore } from './onboardingFlowStore'
import { useImportFlowStore } from './importFlowStore'
import { usePreventiveDueStore } from './preventiveDueStore'
import { usePreventivePlanDraftStore } from './preventivePlanDraftStore'
import { useAlertsStore } from './alertsStore'
import { useResearchJobsStore } from './researchJobsStore'
import { usePlanTasksPartialStore, PLAN_TASKS_PARTIAL_STEP } from './planTasksPartialStore'
import { avisarForaDaTela } from '../lib/os-notification/aviso-do-sistema'
import { useAuthStore } from '../shared/stores/auth'
import { resolveOutboundTenantId } from '../lib/tenant/outboundTenantId'
import type { PreventiveDueCardEvent } from '../features/maintenance-plans/types'
import type { InAppAlertCreatedEvent } from '../types/alerts'
import type {
  PlanResearchProgressEvent,
  PlanResearchReadyEvent,
  PlanResearchFailedEvent,
} from '../types/research-jobs'
import type { WebSearchMeta } from './chatStore'
import type { ConnectionState, TransportMode } from '../lib/shared-types'
import type { ServerEvent, ChatRestResponse } from '../lib/shared-types'
import type { FileRef, OutboundAttachment } from '../lib/engine/protocol'
import { buildOutboundMessage } from '../lib/engine/protocol'
import { enqueue } from '../lib/engine/messageQueue'
import {
  sendChatStream,
  type WebSearchRetryPayload,
  type OnboardingActionWire,
  type ClosingNoteWandActionPayload,
} from '../lib/chat-stream'
import { generateClientRequestId, registerClientRequestId } from '../lib/engine/idempotency'
import { reloadConversationHistory } from '../lib/chat/reload-conversation'
import { parseActionCards } from '../lib/parse-action-cards'
import type { ActionCard } from '../lib/parse-action-cards'
import { parkResponseConfirmation } from '@/stores/park-response-confirmation'
import type { MultiSheetProgress, MultiSheetImportResult } from '../types/import'
import type {
  SuggestedAction,
  ImportJobEnqueuedEvent,
  ImportJobStartedEvent,
  ImportJobProgressEvent,
  ImportJobCompletedEvent,
  ImportJobFailedEvent,
  EquipmentRegistrationFlowStateUpdateEvent,
  RichContent,
  EquipmentRegistrationActionPayload,
  EquipmentRegistrationFlowSnapshot,
  ImportFlowStateUpdateEvent,
  ImportFlowActionWire,
  OnboardingFlowStateUpdateEvent,
  CorrectiveAction,
} from '../types/rich-content'
import type { PreventivePlanAction, PreventivePlanDraftRichContent } from '../types/preventive-plan'
import type { NpsAction } from '../types/nps-pulse'
import type { PlanComparisonAction } from '../types/plan-comparison'
import type { PhotoPolicyAction } from '../types/rich-content'
import i18n from '../lib/i18n/config'
import { handleAsyncTaskEvent, notifyEngineMessageDropped } from '../lib/toast'
import { getEngineRestUrl, getEngineWsConnectUrl } from '../lib/config'
import { marcaOnb } from '../lib/marcas-do-turno'
import { useSugestoesStore } from './sugestoesStore'
import {
  clearLastTurnRequest,
  clearLastTurnRequests,
  getLastTurnRequest,
  recordLastTurnRequest,
} from '@/stores/lastTurnRequest'
import { openInsightsForConversation } from '@/stores/conversationArtifacts'
import type { DashboardSpec } from '@/features/artifacts/types'

// Lista positiva única movida para `./rich-content-card-keys` (STO-6938); reexportada.
import {
  RICH_CONTENT_CARD_KEYS,
  workOrderPresentationFromWs,
  workOrderActionsFromWs,
} from './rich-content-card-keys'
import type { NotificationAction } from '@/lib/chat/clickedActionPresentation'

export { RICH_CONTENT_CARD_KEYS }

/**
 * O que o motor manda em `richContent` que NÃO é canal de card — e por isso não
 * entra em `RICH_CONTENT_CARD_KEYS`, mas TAMBÉM não pode disparar o aviso de
 * "chave desconhecida" abaixo. São dados legítimos com destino próprio (texto do
 * turno, ferramentas, formulário, confirmação por voz), não cards descartados.
 *
 * STO-170 — cada isenção carrega o motivo escrito (quem consome a chave) porque
 * é exatamente essa falta de dono que deixou `preventivePlanSuggestionCard`
 * (STO-76) morar aqui por engano: a chave JÁ tinha um branch de render com 3
 * botões no `MessageBubble` — um card de verdade escondido na lista errada — e
 * nada aqui cobrava a justificativa que teria exposto a contradição. Portão
 * cobrando em `connectionStore.nonCardKeysHaveOwners.test.ts`.
 */
export const KNOWN_NON_CARD_RICH_CONTENT_JUSTIFICATIONS: Readonly<
  Partial<Record<keyof RichContent, string>>
> = {
  proposal:
    'Proposta de importação (planilha/multi-sheet) desenhada como conteúdo da própria bolha ' +
    '(`MessageBubble.tsx`, seções de import), não como card de ação — sem botão próprio, some ' +
    'da lista canônica do motor (`envelope-keys.ts`) e só volta a acender na releitura do ' +
    'histórico persistido (`persisted-presentation.ts`).',
  files:
    'Lista de arquivos anexados ao turno, desenhada dentro da bolha (`MessageBubble.tsx`) como ' +
    'anexo do texto, não como card clicável. Mesma classe de `proposal`: fora do envelope ' +
    'canônico do motor hoje, só reaparece na releitura do histórico persistido.',
  toolsUsed:
    'Linha de transparência de ferramentas (`ToolTrace`, ex-STO-186) montada à mão em ' +
    '`connectionStore.ts` a partir de `toolTrace`/`toolsUsed` do envelope, não repassada pela ' +
    'lista branca genérica — é metadado do turno (o que a IA executou), não um card de ação.',
  formSchema:
    'Formulário embutido na bolha (`FormRenderer` dentro de `MessageBubble.tsx`), consumido pelo ' +
    'próprio turno que o pediu — sem botão de card em volta. Fora do envelope canônico do motor ' +
    'hoje; só acende de novo na releitura do histórico persistido.',
  processing:
    'Texto do indicador de processamento (`ProcessingIndicator` em `MessageBubble.tsx`), escrito à ' +
    'mão em `connectionStore.ts` a cada chunk de progresso do motor — é status do turno em voo, ' +
    'não um card com ação para o técnico.',
  aurisReview:
    'Board de revisão do Auris (`useAurisReviewBoard` em `MessageBubble.tsx`), consumido inteiro ' +
    'pelo próprio hook de revisão — tem interação própria (aprovar/recusar), mas não é um ' +
    '`ActionCard` do canal genérico de botões.',
} as const

const KNOWN_NON_CARD_RICH_CONTENT_KEYS = Object.keys(
  KNOWN_NON_CARD_RICH_CONTENT_JUSTIFICATIONS,
) as ReadonlyArray<keyof RichContent>

const KNOWN_TRANSIENT_RICH_CONTENT_KEYS = ['voiceConfirmation', 'onboardingRailHandoff'] as const

/**
 * Toda chave que o store JÁ CONHECE em `richContent`: os canais de card mais os
 * não-cards legítimos. Serve de lista branca para o aviso de chave desconhecida.
 *
 * Exportada (STO-66, passo 3) para o portão de paridade
 * `connectionStore.richContentEnvelopeParity.test.ts` comparar contra
 * `RICH_CONTENT_ENVELOPE_KEYS` do `@rochadenis/axiomgo-contracts` — a lista
 * CANÔNICA que o motor pode emitir. Sem essa comparação, uma sexta chave
 * esquecida (depois de `listCard`/`listRowDetail`) só apareceria no Sentry
 * DEPOIS de já ter passado por produção; com ela, aparece num teste vermelho
 * antes do merge.
 */
export const KNOWN_RICH_CONTENT_KEYS: ReadonlySet<string> = new Set<string>([
  ...RICH_CONTENT_CARD_KEYS,
  ...KNOWN_NON_CARD_RICH_CONTENT_KEYS,
  ...KNOWN_TRANSIENT_RICH_CONTENT_KEYS,
])

/**
 * Dedupe do aviso: cada chave desconhecida é reportada UMA vez por sessão. Sem
 * isto, um canal novo do motor dispararia um evento Sentry por turno.
 */
const reportedUnknownRichContentKeys = new Set<string>()

/**
 * A lista branca não sabia avisar quando não conhecia uma chave: o motor mandava
 * um canal de card novo (foi assim com `equipmentSearchResult`, `suggestedActions`,
 * `resourceAllocation` — e agora `listCard`/`listRowDetail`, STO-66) e o store o
 * descartava em SILÊNCIO, sem erro, sem Sentry, antes de virar pixel. Este aviso
 * fecha a CLASSE: qualquer chave de `richContent` fora de `KNOWN_RICH_CONTENT_KEYS`
 * vira um evento Sentry (nível `warning`), uma vez por chave, para o próximo canal
 * esquecido aparecer no painel em vez de na tela do usuário. Ver BLOCO 5 da régua:
 * "tipo de card desconhecido tem fallback explícito, visível e registro no Sentry".
 */
export function reportUnknownRichContentKeys(wire: Record<string, unknown>): void {
  for (const key of Object.keys(wire)) {
    const value = wire[key]
    if (value === undefined || value === null) continue
    if (KNOWN_RICH_CONTENT_KEYS.has(key)) continue
    if (reportedUnknownRichContentKeys.has(key)) continue
    reportedUnknownRichContentKeys.add(key)
    console.warn(
      `[ConnectionStore] richContent com chave DESCONHECIDA "${key}" — ` +
        'o motor mandou um canal que o chat não conhece e vai descartar. ' +
        'Se for card, declare-o em RICH_CONTENT_CARD_KEYS + RichContent.',
    )
    Sentry.captureMessage('rich_content_unknown_key', {
      level: 'warning',
      extra: { key, valueType: Array.isArray(value) ? 'array' : typeof value },
    })
  }
}

/**
 * Copia do envelope do fio só os canais declarados acima, preservando o shape
 * verbatim (o chat nunca reescreve o que o motor mandou). `entityCards` é o
 * único com regra extra: lista vazia não é card e não deve ligar o sidecar.
 *
 * O `satisfies` acima é metade do portão: uma chave nesta lista SEM espelho em
 * `RichContent` não compila. A outra metade — chave espelhada e mesmo assim não
 * repassada — é o `connectionStore.richContentPassthrough.test.ts`. A TERCEIRA
 * metade (STO-66): `reportUnknownRichContentKeys` avisa no Sentry quando o motor
 * manda uma chave que a lista branca não conhece, em vez de descartá-la calada.
 */
function pickRichContentCardChannels(
  wire: Record<string, unknown> | undefined,
): Partial<RichContent> {
  const picked: Record<string, unknown> = {}
  if (!wire) return picked
  reportUnknownRichContentKeys(wire)
  for (const key of RICH_CONTENT_CARD_KEYS) {
    const value = wire[key]
    if (value === undefined || value === null) continue
    if (key === 'entityCards' && (!Array.isArray(value) || value.length === 0)) continue
    picked[key] = value
  }
  return picked as Partial<RichContent>
}

/** Test-only: limpa o dedupe do aviso de chave desconhecida entre casos. */
export function _resetUnknownRichContentKeyReportsForTest(): void {
  reportedUnknownRichContentKeys.clear()
}

/**
 * Defense-in-depth: verify that an incoming WS/SSE message belongs to the
 * current tenant before letting it reach the chat store.
 *
 * Returns `true` when the message MUST be discarded (cross-tenant or
 * structurally invalid origin). Returns `false` when the message is safe.
 *
 * Compat: if `msgTenantId` is absent (engine older than tenant-tagging), we
 * emit a warn and pass the message through so a gradual rollout never breaks
 * existing sessions.
 */
function isCrossTenant(msgTenantId: unknown): boolean {
  const currentTenantId = useAuthStore.getState().tenantId
  if (!msgTenantId || typeof msgTenantId !== 'string') {
    console.warn('[ConnectionStore] message without tenantId (compat mode)')
    return false
  }
  if (!currentTenantId) {
    console.warn('[ConnectionStore] no current tenant in auth store')
    return false
  }
  if (msgTenantId !== currentTenantId) {
    console.error('[ConnectionStore] CROSS-TENANT message blocked', {
      msgTenantId,
      currentTenantId,
    })
    Sentry.captureMessage('cross_tenant_blocked', {
      level: 'error',
      extra: { msgTenantId, currentTenantId },
    })
    return true
  }
  return false
}

// Long per-turn SILENCE cap (3 minutes). Like the idle watchdog it is re-armed
// by ANY inbound signal (chunk / any progress / processing / keep-alive — see
// armTurnTimers), so it measures pipe silence, NOT wall-clock duration: a
// corrective turn that runs ~5 serial LLM calls and legitimately exceeds 2 min
// keeps streaming (or keep-aliving) and is never cut. Because it shares the same
// re-arm triggers as the idle watchdog, the idle watchdog is the EFFECTIVE
// detector (it fires first on real silence); this longer cap only becomes the
// binding one if STREAM_IDLE_TIMEOUT is ever raised at/above it. Hung-but-alive
// turns are the engine's job — its 10-min AGENT_RUN_TIMEOUT_MS always emits a
// terminal `error` event that onError surfaces.
const RESPONSE_TIMEOUT = 180_000

// Teto da pesquisa do plano no servidor (espelho do engine, à mão): 300 s.
export const PLAN_RESEARCH_SERVER_CAP_MS = 300_000
// Janela de espera quando o último sinal é um progresso `searching`: o teto do
// servidor mais uma folga pequena. Com 180 s o cliente desistia de uma pesquisa
// que o servidor ainda ia concluir (F4.5). Vale para o SSE (`onProgress`) e para
// o caminho WS legado.
export const WEB_SEARCH_RESPONSE_TIMEOUT = PLAN_RESEARCH_SERVER_CAP_MS + 20_000

// ─── Engine-contract mirror (keep in sync with axiomgo-engine) ──────────────
// The idle watchdog below is DERIVED from the engine's own timing contract, not
// a hand-picked number. These constants mirror the engine so the derivation is
// explicit and a reader sees exactly what to change if the engine retunes.
//
// `ENGINE_FINAL_LLM_CAP_MS` — the engine bounds its FINAL narration call to this
// (axiomgo-engine/src/runtime/final-call-timeout.ts `MAX_FINAL_LLM_MS`). During
// that window the pipe is legitimately CONTENT-SILENT: a provider's
// time-to-first-token, or the fallback-chain switching providers, emits no
// `chunk`/`progress` — only `: keep-alive` comments. The engine ALWAYS emits a
// terminal `response`/`error` by this deadline (on timeout it resolves a
// deterministic fallback), so it is one worst-case *legitimate* single silent gap.
//
// `ENGINE_RESEARCH_API_TIMEOUT_MS` — STO-105, measured 12/09/2026. The OTHER
// worst-case single silent gap, and the sharper one: the deterministic
// preventive-plan/onboarding research turn (`deterministic-turn-router.ts`
// `preventive-plan-structured`, which calls `ensure-research.ts` DIRECTLY —
// no LLM tool loop, so NONE of the `TOOL_EXECUTION_HEARTBEAT_MS` 10s heartbeat
// in `aisdk-sse-adapter.ts` applies) can spend this entire window inside the
// `research-api` fallback provider with ZERO `onProgress` calls — confirmed by
// reading `research-api.ts`: its poll loop never takes an `onProgress`
// parameter at all, unlike the AxiomK poller two files over. Source of the
// number: `axiomgo-engine/src/services/search/research-api.ts:67`
// `RESEARCH_API_TIMEOUT_MS = 60_000`. Before this fix that number was
// EQUAL to `STREAM_IDLE_TIMEOUT` (60s vs 60s) — not nested, tied — so any
// keep-alive delivery hiccup during that provider call false-cut a turn the
// engine was still finishing within its OWN advertised budget. This is the
// STO-105 "candidate mais forte" made concrete: not the 240s outer
// `research_equipment` tool ceiling (that path DOES get the 10s tool
// heartbeat when dispatched through the LLM loop), but this specific
// heartbeat-less inner provider call on the deterministic short-circuit path.
//
// `ENGINE_KEEPALIVE_INTERVAL_MS` — the engine's SSE route writes `: keep-alive`
// on this interval (axiomgo-engine/src/routes/chat-stream.ts). It is the
// best-effort liveness signal the front re-arms on (see armTurnTimers).
const ENGINE_FINAL_LLM_CAP_MS = 65_000
const ENGINE_RESEARCH_API_TIMEOUT_MS = 60_000
const ENGINE_KEEPALIVE_INTERVAL_MS = 10_000

// The engine-side worst-case *legitimate* single silent gap, whichever of the
// two known contributors above is larger. Take the max, not either one alone:
// a smaller watchdog than either origin reopens exactly the false-cut this
// constant exists to close.
const ENGINE_WORST_CASE_SILENT_GAP_MS = Math.max(
  ENGINE_FINAL_LLM_CAP_MS,
  ENGINE_RESEARCH_API_TIMEOUT_MS,
)

// Inactivity watchdog — the front's correctness boundary for a DEAD pipe.
// Re-armed on EVERY inbound SSE event (chunk / progress / processing / keep-alive,
// see armTurnTimers). It MUST exceed the engine's worst-case *legitimate* silent
// window (`ENGINE_WORST_CASE_SILENT_GAP_MS`); otherwise a keep-alive delivery
// hiccup — CDN buffering (Railway/Fastly), an event-loop stall, or network
// jitter batching the 10s pings — during that window false-cuts a turn the
// engine is still finishing within its own budget, surfacing "A resposta
// demorou demais" mid-turn. Derived, not guessed: worst-case gap (65s, the
// final-LLM cap — STO-755/STO-219, binding again since the engine retuned
// `MAX_FINAL_LLM_MS` from 45s to 65s; the research-api ceiling, 60s, is no
// longer the larger of the two) + 15s of slack above it = 80s. The slack
// absorbs the gap between the front's
// last re-arm and the engine starting its own clock, plus serialization +
// return-transit of the terminal event, plus general scheduling jitter — one
// keep-alive interval (10s) + a 5s transit margin sizes it. The keep-alive
// re-arm is the fast-path liveness signal; this watchdog is the backstop that
// stays correct even under TOTAL keep-alive loss. Still far below
// RESPONSE_TIMEOUT (180s), so it remains the effective silence detector.
//
// All engine constants are exported alongside so a unit test can assert the
// front-side floor `STREAM_IDLE_TIMEOUT >= worst-case gap + keep-alive
// interval`. NOTE: that test only guards front-side self-consistency (the
// mirror vs. the derived value), NOT equality with the engine's live value —
// the mirrors above must be kept in lockstep with the engine by hand (there is
// no shared timing contract to import).
export const STREAM_IDLE_TIMEOUT =
  ENGINE_WORST_CASE_SILENT_GAP_MS + ENGINE_KEEPALIVE_INTERVAL_MS + 5_000
export { ENGINE_FINAL_LLM_CAP_MS, ENGINE_RESEARCH_API_TIMEOUT_MS, ENGINE_KEEPALIVE_INTERVAL_MS }

// `ENGINE_AGENT_RUN_TIMEOUT_MS` — the engine's own ceiling on ONE agent run
// (axiomgo-engine `AGENT_RUN_TIMEOUT_MS`). Mirrored by hand, like the two
// constants above; there is no shared timing contract to import.
const ENGINE_AGENT_RUN_TIMEOUT_MS = 10 * 60_000

/**
 * Wall-clock ceiling on a single turn. **No inbound signal re-arms it** — that
 * is the entire point, and it is what makes this different from the two
 * watchdogs above.
 *
 * Denis, 18/08 22:59: _"ele continua TRAVANDO nesse elemento de raciocínio."_
 * Both watchdogs measure pipe SILENCE and `onKeepAlive` re-arms both. The
 * engine's SSE route writes `: keep-alive` every 10s from the ROUTE, so it
 * keeps flowing even when the agent behind it has wedged or died without
 * emitting a terminal event — which is what the plan densifier did on 18/08
 * 01:34 (blew its budget, died silent). Every keep-alive pushed both deadlines
 * out, so neither could ever fire and the turn had NO wall-clock bound: the
 * reasoning indicator span forever. Reproduced deterministically, 5/5 runs, in
 * `connectionStore.turnNeverHangsForever.test.ts`.
 *
 * The previous code delegated this case to the backend — _"Hung-but-alive turns
 * are the engine's job — its 10-min AGENT_RUN_TIMEOUT_MS always emits a
 * terminal `error` event"_. That is a promise the frontend cannot verify, and a
 * UI that spins forever whenever a backend promise is broken is a frontend
 * defect regardless of who broke the promise.
 *
 * Sized ABOVE the engine's own cap on purpose. When the engine behaves, its
 * terminal event always lands first and this timer never fires — so it cannot
 * false-cut a legitimately long turn. Cutting earlier would trade a rare
 * infinite spinner for frequent premature failures, and in this product a
 * premature failure invites a re-click, which is how duplicate records get
 * written. This is the backstop, not the primary deadline: the 60s idle
 * watchdog still handles every dead-pipe case first.
 */
export const TURN_WALL_CLOCK_TIMEOUT = ENGINE_AGENT_RUN_TIMEOUT_MS + 30_000
export { ENGINE_AGENT_RUN_TIMEOUT_MS }

/** Ensure the WS URL always ends with /ws path */
function ensureWsPath(url: string): string {
  if (url.endsWith('/ws')) return url
  return url.replace(/\/+$/, '') + '/ws'
}

/**
 * Keyed options keep action channels separate from presentation and content.
 * Plan-edit clicks use onboardingAction, never a parallel planEditAction.
 */
export interface SendRawOptions {
  clickedActionLabel?: string
  fileRefs?: FileRef[]
  inlineAttachments?: OutboundAttachment[]
  /** PR8.5 Gap A: voice-originated turns; engine gate keys on this. */
  commandSource?: 'voice'
  /** PR-E: single-use confirmation token, replayed verbatim. */
  confirmationToken?: string
  /**
   * Chip de indicador — id do registro de intents do motor. Presente so quando
   * a pessoa CLICOU um chip; texto digitado nunca o carrega. Ver
   * `sugestoesStore.ts` para o defeito medido que o gerou.
   */
  intentId?: string
  /** E3 + Trilho §4.3 — accepts LEGACY card kind AND rail actionId+payload. */
  onboardingAction?: OnboardingActionWire
  /** #426 — card-driven equipment-registration action. */
  equipmentRegistrationAction?: EquipmentRegistrationActionPayload
  /** #426 C9 — refine/retry search refresh signal. */
  webSearchRetry?: WebSearchRetryPayload
  /** Fase 2 Camada A — deterministic preventive-plan builder action. */
  preventivePlanAction?: PreventivePlanAction
  orderExecutionAction?: import('../types/rich-content').OrderExecutionAction
  /** Procedimento com foto — clique estruturado do cartão `procedureAuthoring`. */
  procedureAuthoringAction?: import('@/features/procedimento-e-foto/procedimento/types').ProcedureAuthoringAction
  /** B4 fatia 4b — resposta do pulse NPS 0-10 (canal estruturado, sem LLM). */
  npsAction?: NpsAction
  /** Fatia 2 da máquina de importação — ação estruturada do card do ImportFlow. */
  importFlowAction?: ImportFlowActionWire
  /** Paleta de comandos do campo de digitar (etapa 1, R10) — id do comando
   *  escolhido na barra. Repassado verbatim para `sendChatStream`. */
  command?: string
  /** CORR-B F3 — nota de encerramento de operação enviada junto com o
   *  confirmationToken. Omitida em todo turno sem nota (campo ausente = corpo
   *  byte-a-byte igual ao de um turno normal). */
  closeNote?: string
  /** CORR-B F4b (STO-721) — the wand click. Omitted on every turn that isn't one. */
  closingNoteWandAction?: ClosingNoteWandActionPayload
  /** Structured corrective opening; content stays empty. */
  correctiveAction?: CorrectiveAction
  notificationAction?: NotificationAction
  /** Clique "Assumir como plano oficial" do cartão `planComparison`. */
  planComparisonAction?: PlanComparisonAction
  /** Clique "Salvar política" do cartão `photoPolicyCard` (contrato 0.30.0). */
  photoPolicyAction?: PhotoPolicyAction
  agentAction?: AgentAction
}

interface ConnectionStore {
  state: ConnectionState
  transport: TransportMode
  retryCount: number
  lastError: string | null
  manager: ConnectionManager | null
  tenantId: string
  userId: string

  initialize: (tenantId: string, userId: string) => void
  sendMessage: (content: string, conversationId?: string) => Promise<ChatRestResponse>
  /**
   * Fire-and-forget send. Messages with fileRefs (uploaded spreadsheets)
   * or legacy inlineAttachments (images, pdf, docx) route through the
   * same SSE path — there is no longer a WS-for-attachments branch
   * (ADR-004 phase 1).
   *
   * PR8.5 Gap A: pass `options.commandSource: 'voice'` for voice-originated turns so
   * the engine's critical-confirmation gate can key on it. Omit for all
   * normal chat turns — the body must remain byte-for-byte unchanged.
   *
   * PR-E: pass `options.confirmationToken` (verbatim from pendingConfirmation) for
   * single-use replay turns. Absent ⇒ field is omitted so all non-replay
   * turns are byte-for-byte unchanged.
   */
  sendRaw: (
    conversationId: string,
    content: string,
    messageId: string,
    options?: SendRawOptions,
    retry?: boolean,
  ) => boolean
  /** Send file_upload WS message after REST upload returns a fileId */
  sendFileUpload: (conversationId: string, fileId: string) => boolean
  /**
   * Abort the active SSE stream (if any).
   * Clears typing indicator, progress bar, and marks the in-flight streaming
   * bubble as cancelled so the UI can render it in a degraded-but-visible state.
   */
  abortCurrentStream: () => void
  disconnect: () => void

  isConnected: () => boolean
  isUsable: () => boolean
  statusText: () => string
  statusColor: () => string
}

/** Loose UUID shape check — accepts both standard UUID v4 and `crypto.randomUUID()`. */
const UUID_SHAPE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
function isUuidShape(value: unknown): value is string {
  return typeof value === 'string' && UUID_SHAPE.test(value)
}

/** Module-level response timeout — hard per-turn wall-clock cap */
let responseTimer: ReturnType<typeof setTimeout> | null = null
/** Module-level inactivity watchdog — re-armed on every incoming SSE event */
let idleWatchdogTimer: ReturnType<typeof setTimeout> | null = null
/**
 * Module-level wall-clock deadline for the turn. Armed ONCE per turn in
 * `sendRaw` and never re-armed — see TURN_WALL_CLOCK_TIMEOUT. Deliberately not
 * touched by `armTurnTimers`: if a liveness signal could push it out, it would
 * be a third silence watchdog instead of the guarantee that a turn terminates.
 */
let turnDeadlineTimer: ReturnType<typeof setTimeout> | null = null
/**
 * O turno em voo em HORA DE PAREDE — a metade do prazo que um relógio congelado
 * não consegue guardar.
 *
 * `turnDeadlineTimer` acima é um `setTimeout`, e o iPadOS **congela os
 * temporizadores da aba** quando ela vai para segundo plano. Congelado, ele não
 * dispara: some e não volta, porque é o único dos três freios sem rearme. Isto
 * aqui é a mesma promessa guardada num relógio que a suspensão não para.
 * Ver `darPorPerdidoSeAAbaFicouForaDemais`.
 */
let turnInFlight: {
  conversationId: string
  startedAtMs: number
  content: string
  options?: Omit<SendRawOptions, 'confirmationToken'>
} | null = null
let pendingMessageConvId = ''

/** Module-level abort controller for the active SSE stream. Replaced on every sendRaw call. */
let currentAbortController: AbortController | null = null

/**
 * Para os três temporizadores do turno, SEM apagar `turnInFlight`.
 *
 * STO-214: existe um caminho terminal que não pode apagar o rastro do turno —
 * o `onError` do SSE (o fio morreu). O motor pode terminar o turno DEPOIS de o
 * socket cair (medido: socket morto 17:33:11, turno concluído 17:34:18), e
 * `turnInFlight` é a ÂNCORA que permite a volta ao primeiro plano ainda
 * confirmar com o servidor se a resposta chegou — ver
 * `reconciliarTurnoEmVooComOServidor`. Zerar os temporizadores aqui evita que
 * eles disparem tarde (o aviso local que o `onError` já escreveu não precisa
 * de um segundo aviso por cima); manter `turnInFlight` é o que faltava para o
 * caminho de volta ter gatilho.
 */
function pararTemporizadoresDoTurno() {
  // Todo caminho terminal do turno passa aqui (resposta, erro, cancelamento,
  // vigias). As tarefas provisórias do plano são do turno em voo: o card final
  // é o dono do número, e provisório + final nunca aparecem juntos.
  usePlanTasksPartialStore.getState().limpaTudo()
  if (responseTimer) {
    clearTimeout(responseTimer)
    responseTimer = null
  }
  if (idleWatchdogTimer) {
    clearTimeout(idleWatchdogTimer)
    idleWatchdogTimer = null
  }
  if (turnDeadlineTimer) {
    clearTimeout(turnDeadlineTimer)
    turnDeadlineTimer = null
  }
}

/** A late credential must not leave watchdogs that can write into the next identity. */
function settleStaleCredentialTurn(epoch: number): void {
  if (!isCurrentTurn(epoch)) return
  clearTurnTimers()
  settleCurrentTurn()
  currentAbortController?.abort()
}

/** Clear every turn timer (call on success, abort, and watchdog expiry). */
function clearTurnTimers() {
  pararTemporizadoresDoTurno()
  // Todo caminho terminal passa por aqui, então é aqui que o turno deixa de
  // estar em voo — sem isto, voltar para a aba depois de uma resposta bem
  // sucedida transformaria um turno respondido em falha.
  turnInFlight = null
}

/**
 * A CONFERÊNCIA COM O SERVIDOR (STO-214) — o histórico do banco sempre vence
 * o estado fabricado no cliente (indicador de "pensando" ainda aceso, bolha
 * de erro provisória escrita por `onError`/`failTurn`).
 *
 * Busca o histórico da conversa; se o servidor devolveu uma resposta do
 * assistente que a tela ainda não tinha, o turno estava resolvido do lado de
 * lá o tempo todo — o `reloadConversationHistory` já injetou a bolha real na
 * store, e esta função só precisa desligar o indicador e remover o artefato
 * provisório (bolha `isStreaming`/`status:'error'`) que ficaria por cima dela.
 *
 * Devolve `true` quando resolveu (o chamador não deve mais escrever falha
 * local), `false` quando o servidor não tinha nada novo (o turno pode
 * legitimamente continuar em voo, ou o chamador segue para o aviso local).
 */
async function reconciliarTurnoEmVooComOServidor(conversationId: string): Promise<boolean> {
  const { novaRespostaChegou } = await reloadConversationHistory(
    conversationId,
    useChatStore.getState().ingestConversationHistory,
  )
  if (!novaRespostaChegou) return false

  clearTurnTimers()
  settleCurrentTurn()
  const chat = useChatStore.getState()
  chat.setIsTyping(false)
  chat.clearProgress()
  // A bolha provisória do turno que a releitura acabou de resolver: a
  // parcial ainda em `isStreaming`, ou o aviso de erro que `onError`/
  // `failTurn` já tinham escrito. A resposta real já entrou pela releitura
  // acima — este artefato só ficaria por cima dela, duplicando o turno.
  const bolhaProvisoria = chat.messages.find(
    (m) =>
      m.conversationId === conversationId &&
      m.role === 'assistant' &&
      (m.isStreaming === true || m.status === 'error'),
  )
  if (bolhaProvisoria) chat.removeMessage(bolhaProvisoria.id)
  return true
}

/**
 * Arm the turn's wall-clock deadline. Called ONCE, at turn start. There is no
 * re-arm path on purpose — every terminal path goes through `clearTurnTimers`,
 * so the only way this fires is a turn that never terminated.
 */
function startTurnDeadline(
  conversationId: string,
  content: string,
  options?: Omit<SendRawOptions, 'confirmationToken'>,
) {
  if (turnDeadlineTimer) clearTimeout(turnDeadlineTimer)
  turnInFlight = { conversationId, startedAtMs: Date.now(), content, options }
  turnDeadlineTimer = setTimeout(() => {
    turnDeadlineTimer = null
    failTurn(conversationId, 'chat.turnGaveUp')
  }, TURN_WALL_CLOCK_TIMEOUT)
}

/**
 * O FREIO QUE SOBREVIVE À SUSPENSÃO DA ABA — o conserto do congelamento no iPad.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * O DEFEITO
 * ══════════════════════════════════════════════════════════════════════════
 * A pessoa aperta um botão da conversa, sai do navegador por um instante —
 * troca de aplicativo, trava a tela, chega uma notificação — e quando volta o
 * "PENSANDO" continua girando. Para sempre.
 *
 * O iPadOS **congela os relógios da página** numa aba de segundo plano, e os
 * três freios desta tela são todos `setTimeout`: o vigia de silêncio (60 s), o
 * teto por turno (180 s) e o último recurso (630 s). Congelam juntos. O de
 * 630 s é o pior, porque é o único **sem rearme**: some e não volta.
 *
 * E o freio do MOTOR não cobre isto — ele corta em 620 s e escreve a frase
 * honesta num cano que já não tem ninguém do outro lado lendo.
 *
 * A lacuna já estava escrita neste arquivo, dentro do `sendRaw`: _"isto NÃO
 * cobre a suspensão de aba do iPadOS — suspender não é trocar de rede, e
 * `onLine` continua verdadeiro."_ Ninguém a tinha fechado.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * POR QUE O CONSERTO É ESTE, E NÃO OUTRO
 * ══════════════════════════════════════════════════════════════════════════
 * O único instante em que a página volta a executar código depois da suspensão
 * é a volta da aba. Então é aqui que se pergunta o que um relógio congelado não
 * soube perguntar: **passou do prazo em hora de parede?** `Date.now()` mede o
 * mundo, não o processador da aba, e por isso atravessa a suspensão intacto.
 *
 * Falha FECHADA e em voz alta: a pessoa recebe o mesmo aviso que receberia se o
 * relógio tivesse rodado, e o mesmo botão de tentar de novo. Nunca silêncio.
 *
 * **Não é um quarto vigia de silêncio.** Ele não reabre prazo nenhum, não é
 * rearmado por sinal de vida, e só sabe uma coisa: quanto tempo de mundo passou
 * desde que o turno começou. Suspensão curta não mata turno bom — é o outro
 * lado da régua, e tem trava própria.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * STO-214 — A VOLTA AO PRIMEIRO PLANO CONFERE COM O SERVIDOR, SEMPRE
 * ══════════════════════════════════════════════════════════════════════════
 * A metade acima (2026-08/09) só perguntava "estourou o prazo?" — e quando a
 * resposta era "não ainda", não fazia mais nada. Isso deixava um buraco: o
 * motor grava a resposta no banco incondicionalmente (`turn-epilogue.ts` /
 * `ConversationOrchestrator.ts`), mas nada na tela ia buscá-la além da
 * PRÓXIMA troca de conversa — medido em 62min50s de spinner morto com a
 * resposta pronta do outro lado (STO-214, via Loki, na STO-205).
 *
 * Agora TODA volta ao primeiro plano com um turno em voo confere a verdade do
 * servidor primeiro (`reconciliarTurnoEmVooComOServidor`), antes de perguntar
 * sobre o prazo. Se o motor já tinha terminado — mesmo dentro do prazo, mesmo
 * que o fio SSE tenha morrido no meio do caminho (`onError` mantém
 * `turnInFlight` vivo exatamente para isto, ver `pararTemporizadoresDoTurno`)
 * — a resposta real substitui o indicador ou a bolha de erro provisória. Só
 * quando o servidor não tem nada novo E o prazo de parede já estourou é que a
 * desistência local (`failTurn`) ainda entra em cena.
 */
async function darPorPerdidoSeAAbaFicouForaDemais(): Promise<void> {
  if (typeof document === 'undefined') return
  if (document.visibilityState !== 'visible') return
  const emVoo = turnInFlight
  if (emVoo === null) return

  const resolvidoPeloServidor = await reconciliarTurnoEmVooComOServidor(emVoo.conversationId)
  // Enquanto esperávamos a rede, outra coisa pode ter decidido o turno (uma
  // resposta chegou pelo SSE normal, um retry começou outro turno...). A
  // identidade do objeto `emVoo` é a prova: se `turnInFlight` mudou, quem
  // decidiu já cuidou do estado e esta função não tem mais nada a fazer.
  if (turnInFlight !== emVoo) return
  if (resolvidoPeloServidor) return

  if (Date.now() - emVoo.startedAtMs < TURN_WALL_CLOCK_TIMEOUT) return
  failTurn(emVoo.conversationId, 'chat.turnGaveUp')
}

// Registrado no carregamento do módulo, e não em `initialize`: o congelamento
// não pede licença para acontecer, e uma aba pode ser suspensa antes de
// qualquer conexão existir. Um ouvinte só, para a vida inteira da página.
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    void darPorPerdidoSeAAbaFicouForaDemais()
  })
}

/**
 * Fail the current turn gracefully: clear spinner, abort stream, surface a single
 * recoverable failure state. Centralised so all expiry paths (idle watchdog,
 * response-timeout) use the same code without drift.
 *
 * Mirrors the onError either/or reconciliation: if tokens had already started
 * streaming, finalize THAT bubble to `error` (stops the typewriter cursor, keeps the
 * partial answer visible) instead of leaving it `isStreaming:true` forever with a
 * separate error message dangling below. Only when no bubble exists do we add the
 * standalone timeout message. failTurn is module-level and cannot read the per-send
 * streamingMessageId closure, so it locates the in-flight bubble via the store — the
 * same lookup abortCurrentStream uses.
 */
function failTurn(conversationId: string, i18nKey: string, sentryTag?: string) {
  clearTurnTimers()
  settleCurrentTurn()
  // INSTRUMENTAÇÃO (2026-08-31) — só o chamador do VIGIA DE SILÊNCIO passa
  // etiqueta; os outros dois (teto de relógio, tempo de resposta) seguem sem,
  // de propósito, porque o sprint pediu DUAS etiquetas e não é hora de
  // alargar. O `clearTurnTimers()` + `settleCurrentTurn()` acima já garantem
  // que este ponto roda no máximo uma vez por turno: o temporizador que
  // disparou se anula, e os demais são limpos antes de qualquer captura.
  if (sentryTag) {
    Sentry.captureMessage(sentryTag, {
      level: 'warning',
      tags: { stream_outcome: sentryTag },
    })
  }
  // Abort the SSE connection so it doesn't linger in the background.
  if (currentAbortController) {
    currentAbortController.abort()
    currentAbortController = null
  }
  const chat = useChatStore.getState()
  chat.setIsTyping(false)
  chat.clearProgress()
  const streamingMsg = useChatStore
    .getState()
    .messages.find((m) => m.isStreaming === true && m.role === 'assistant')
  if (streamingMsg) {
    // Keep every token the engine did manage to send — the user does not lose
    // what was already on screen — and append the reason it stopped, so the
    // bubble states the outcome instead of just going quiet. `status: 'error'`
    // is what surfaces MessageBubble's "Tentar novamente" CTA.
    const reason = i18n.t(i18nKey)
    const partial = streamingMsg.content
    chat.finalizeStreamingMessage(streamingMsg.id, {
      status: 'error',
      content: partial.trim().length > 0 ? `${partial.trimEnd()}\n\n${reason}` : reason,
    })
    return
  }
  chat.addMessage({
    id: `timeout-${Date.now()}`,
    conversationId,
    content: i18n.t(i18nKey),
    role: 'assistant',
    timestamp: Date.now(),
    status: 'error',
  })
}

function pauseTurnForInactivity(conversationId: string) {
  const sentContent = turnInFlight?.content ?? ''
  const sentOptions = turnInFlight?.options
  clearTurnTimers()
  settleCurrentTurn()
  Sentry.captureMessage('stream_idle_timeout', {
    level: 'warning',
    tags: { stream_outcome: 'stream_idle_timeout' },
  })
  if (currentAbortController) {
    currentAbortController.abort()
    currentAbortController = null
  }

  const chat = useChatStore.getState()
  chat.setIsTyping(false)
  chat.clearProgress()
  chat.clearStreamError()

  const streamingMsg = chat.messages.find(
    (message) => message.conversationId === conversationId && message.isStreaming === true,
  )
  if (streamingMsg) {
    chat.removeMessage(streamingMsg.id)
  }

  chat.setInactivityNotice({
    conversationId,
    pausedAt: Date.now(),
    resumeContent: sentContent,
    resumeOptions: sentOptions,
  })
}

/**
 * Arm (or re-arm) the inactivity watchdog for the current turn.
 * Call this on every inbound SSE event so a long-running but active
 * tool never trips the watchdog.
 */
function armIdleWatchdog(conversationId: string) {
  if (idleWatchdogTimer) {
    clearTimeout(idleWatchdogTimer)
  }
  idleWatchdogTimer = setTimeout(() => {
    idleWatchdogTimer = null
    pauseTurnForInactivity(conversationId)
  }, STREAM_IDLE_TIMEOUT)
}

// Keep clearResponseTimer as an alias for backward-compat with the
// WS-branch message handlers that still call it on `response` / `error`.
function clearResponseTimer() {
  if (responseTimer) {
    clearTimeout(responseTimer)
    responseTimer = null
  }
}

function startResponseTimer(conversationId: string, budgetMs: number = RESPONSE_TIMEOUT) {
  clearResponseTimer()
  pendingMessageConvId = conversationId
  responseTimer = setTimeout(() => {
    responseTimer = null
    // RN-2 fix: hard cap must also clear the spinner and abort — not just add a message.
    failTurn(pendingMessageConvId, 'errors.timeout')
  }, budgetMs)
}

/**
 * Re-arm BOTH per-turn watchdogs at once: the idle watchdog and the 180s
 * hard-cap. Both measure *pipe silence* — every inbound signal (chunk, any
 * progress, processing, keep-alive) proves the engine is alive and resets them.
 * The turn only fails on TRUE silence (a dead pipe: no traffic at all); a turn
 * that hangs while still alive is the engine's responsibility (its own LLM
 * timeouts end it). This collapses the per-step special-casing of #368/#369
 * into one uniform rule and is the root fix for the post-tool silent-window race.
 */
function armTurnTimers(conversationId: string, budgetMs: number = RESPONSE_TIMEOUT) {
  armIdleWatchdog(conversationId)
  startResponseTimer(conversationId, budgetMs)
}

/**
 * Sinal de vida que chega pelo WebSocket FORA do cano do turno (`import_job_*`,
 * `import_progress`, `processing`/`progress` legados). Só prova vida de um turno
 * que EXISTE: sem turno em voo (`responseTimer` nulo), não arma vigia nenhum.
 * QA ST-Operations-os#144 B6 — a importação é REST e o motor empurra o andamento
 * por aqui; armar o vigia sem turno deixava um relógio que ninguém desliga, e ele
 * pausava a conversa por "inatividade" no meio do fluxo seguinte.
 */
function rearmarSeHaTurnoEmVoo(conversationId: string, budgetMs: number = RESPONSE_TIMEOUT) {
  if (!responseTimer) return
  armIdleWatchdog(conversationId)
  startResponseTimer(conversationId, budgetMs)
}

export const useConnectionStore = create<ConnectionStore>((set, get) => ({
  state: 'DISCONNECTED',
  transport: 'offline',
  retryCount: 0,
  lastError: null,
  manager: null,
  tenantId: '',
  userId: '',

  initialize: (tenantId: string, userId: string) => {
    if (!tenantId || !userId) {
      console.warn('[ConnectionStore] tenantId ou userId vazio — não inicializando')
      return
    }

    const prev = get().manager
    if (prev) prev.destroy()

    set({ tenantId, userId })

    const manager = new ConnectionManager(
      {
        wsUrl: ensureWsPath(getEngineWsConnectUrl()),
        restUrl: getEngineRestUrl(),
      },
      {
        onStateChange: (state: ConnectionState, transport: TransportMode) => {
          set(state === 'CONNECTED' ? { state, transport, retryCount: 0 } : { state, transport })
        },
        onRetryChange: (count: number) => {
          set({ retryCount: count })
        },
        onMessage: (_data: ServerEvent) => {
          // Typed messages handled via onRawMessage for broader compatibility
        },
        onRawMessage: (data: unknown) => {
          try {
            const msg = data as Record<string, unknown>
            const msgType = msg['type'] as string
            const chat = useChatStore.getState()

            if (msgType === 'connected') {
              const serverId = msg['serverId']
              if (typeof serverId === 'string' && serverId.length > 0) {
                sessionStorage.setItem('axiomgo-engine-session-id', serverId)
              }
              console.log('[ConnectionStore] Engine session:', serverId)
              return
            }

            if (msgType === 'processing') {
              chat.setIsTyping(true)
              chat.clearProgress()
              // Engine working again — drop any stale stream error.
              chat.clearStreamError()
              // Re-arm both timers: engine is still active.
              rearmarSeHaTurnoEmVoo(pendingMessageConvId)
              return
            }

            if (msgType === 'progress') {
              const step = msg['step'] as string
              const detail = msg['detail'] as string
              if (step === PLAN_TASKS_PARTIAL_STEP) {
                usePlanTasksPartialStore.getState().recebe(pendingMessageConvId, msg['data'])
                rearmarSeHaTurnoEmVoo(pendingMessageConvId, WEB_SEARCH_RESPONSE_TIMEOUT)
                return
              }
              if (step && detail) {
                chat.setProgress(step as Parameters<typeof chat.setProgress>[0], detail)
              }
              // Re-arm both timers: progress event = engine is alive.
              rearmarSeHaTurnoEmVoo(
                pendingMessageConvId,
                step === 'searching' ? WEB_SEARCH_RESPONSE_TIMEOUT : RESPONSE_TIMEOUT,
              )
              return
            }

            if (msgType === 'response' || msgType === 'chat_response') {
              if (isCrossTenant(msg['tenantId'])) return
              // Turn succeeded via WS: clear both timers.
              clearTurnTimers()
              chat.setIsTyping(false)
              chat.clearProgress()
              // Turn succeeded — clear any stream error from a prior attempt.
              chat.clearStreamError()
              const convId = chat.activeConversationId || (msg['conversationId'] as string) || ''
              const content =
                (msg['content'] as string) ||
                ((msg['message'] as Record<string, unknown>)?.['content'] as string) ||
                ''

              parkResponseConfirmation(msg)
              const wsSuggestedActions = workOrderActionsFromWs(msg)

              // Sprint 1A — extract actionCards from server payload.
              // Engine sends actionCards at the top level of the WS payload
              // (gateway/server.ts + chat-stream.ts). Legacy/REST envelopes may
              // nest it under metadata. Try top level first, then metadata, then
              // inline ACTION_CARDS block in content (parseActionCards) as last resort.
              const serverMetadata = msg['metadata'] as Record<string, unknown> | undefined
              const serverActionCards = Array.isArray(msg['actionCards'])
                ? (msg['actionCards'] as ActionCard[])
                : Array.isArray(serverMetadata?.['actionCards'])
                  ? (serverMetadata!['actionCards'] as ActionCard[])
                  : undefined
              const parsed = parseActionCards(content, serverActionCards)
              const actionCards = parsed.cards ?? undefined
              // Use parsed.content (strips inline block if present) as display content
              const displayContent = parsed.content

              chat.addMessage({
                id: `engine-${Date.now()}`,
                conversationId: convId,
                content: displayContent,
                role: 'assistant',
                timestamp: Date.now(),
                status: 'sent',
                richContent: workOrderPresentationFromWs(msg),
                webSearchMeta: msg['webSearch'] ? (msg['webSearch'] as WebSearchMeta) : undefined,
                suggestedActions: wsSuggestedActions,
                metadata: actionCards ? { actionCards } : undefined,
              })

              if (convId) {
                chat.updateConversation(convId, { updatedAt: Date.now() })
              }
              return
            }

            // Phase 4b — Import job lifecycle events
            // Card-204 — enqueued snapshot is the first WS event after
            // confirm_import_proposal returns. Without this the card sat
            // at "Confirmar?" until the worker emitted started, which can
            // take seconds under tenant fairness throttling.
            if (msgType === 'import_job_enqueued') {
              // Augment the engine-sent event with the client-side conversationId
              // (pendingMessageConvId = the conv that triggered the send) so the
              // snapshot carries the originating thread. The supervisor will use
              // snap.conversationId when emitting the terminal summary. (#307 fix)
              const enqueuedMsg = msg as unknown as ImportJobEnqueuedEvent
              useImportJobStore.getState().applyEnqueued({
                ...enqueuedMsg,
                conversationId: pendingMessageConvId || undefined,
              })
              rearmarSeHaTurnoEmVoo(pendingMessageConvId)
              return
            }

            if (msgType === 'import_job_started') {
              useImportJobStore.getState().applyStarted(msg as unknown as ImportJobStartedEvent)
              // Re-arm both timers — import job is starting.
              rearmarSeHaTurnoEmVoo(pendingMessageConvId)
              return
            }

            if (msgType === 'import_job_progress') {
              useImportJobStore.getState().applyProgress(msg as unknown as ImportJobProgressEvent)
              // Re-arm both timers — import is actively progressing.
              rearmarSeHaTurnoEmVoo(pendingMessageConvId)
              return
            }

            if (msgType === 'import_job_completed') {
              const completedEvent = msg as unknown as ImportJobCompletedEvent
              useImportJobStore.getState().applyCompleted(completedEvent)
              // ImportJobSupervisor (mounted in ChatPage) is the single emitter of
              // the import_job_summary message — it watches the store and emits once
              // when the job reaches a terminal state.
              return
            }

            if (msgType === 'import_job_failed') {
              const failedEvent = msg as unknown as ImportJobFailedEvent
              useImportJobStore.getState().applyFailed(failedEvent)
              // Same as above — supervisor owns emission.
              return
            }

            // #426 — EquipmentRegistrationFlow live state-update. The engine
            // pushes this whenever the EquipmentRegistrationFlow state machine
            // advances (plant, location, tag, criticality, confirming, done).
            // Tenant guard first — a cross-tenant frame must never mutate the
            // mounted EquipmentRegistrationCard's live snapshot. Routes to
            // applyStateUpdate, which swaps the snapshot by flowId so the card
            // advances live without a reload.
            if (msgType === 'equipment_registration_flow_state_update') {
              if (isCrossTenant(msg['tenantId'])) return
              const flowUpdate = msg as unknown as EquipmentRegistrationFlowStateUpdateEvent
              useEquipmentRegistrationFlowStore.getState().applyStateUpdate(flowUpdate)
              return
            }

            // Fatia 2 da máquina de importação — ImportFlow live state-update.
            // O motor empurra a cada transição da máquina (choosing_path até os
            // terminais). Guarda de tenant primeiro, como em todo handler WS.
            if (msgType === 'import_flow_state_update') {
              if (isCrossTenant(msg['tenantId'])) return
              const importFlowUpdate = msg as unknown as ImportFlowStateUpdateEvent
              useImportFlowStore.getState().applyStateUpdate(importFlowUpdate)
              return
            }

            // C1 — OnboardingFlow live state-update. Quando o import de
            // onboarding termina, o motor empurra este frame (flow-broadcaster.ts)
            // com a máquina em `review` e os totais reais (assetsFound/systemsFound).
            // SEM este handler o frame caía no chão e o card "Encontrei N ativos
            // em M sistemas" nunca aparecia — o card congelava. Guarda de tenant
            // primeiro, como em todo handler WS: frame de outro inquilino nunca
            // pode montar o card de revisão desta tela. Roteia para o store, que
            // o card de revisão subscreve por conversationId.
            if (msgType === 'onboarding_flow_state_update') {
              if (isCrossTenant(msg['tenantId'])) return
              const onboardingUpdate = msg as unknown as OnboardingFlowStateUpdateEvent
              useOnboardingFlowStore.getState().applyStateUpdate(onboardingUpdate)
              return
            }

            // Bloco 3.3 — sino in-app. O motor empurra `in_app_alert.created`
            // quando um alerta é criado e fanned-out para este usuário. Guarda
            // de tenant primeiro: um frame de outro tenant nunca pode mexer no
            // contador do sino.
            //
            // O store decide o que fazer (mover o contador, não montar linha —
            // ver alertsStore.applyPush). Aqui NÃO se abre painel, não se emite
            // toast e não se rouba foco: o sino é ferramenta, não interrupção.
            if (msgType === 'in_app_alert.created') {
              if (isCrossTenant(msg['tenantId'])) return
              const alertEvent = msg as unknown as InAppAlertCreatedEvent
              useAlertsStore.getState().applyPush(alertEvent)
              // STO-354/STO-359 — fora da tela, o mesmo alerta também vira
              // banner do SISTEMA OPERACIONAL. `avisarForaDaTela` decide
              // sozinho se avisa (aba visível não leva banner — ver comentário
              // acima) e nunca lança.
              void avisarForaDaTela({
                titulo: alertEvent.title,
                corpo: i18n.t('osNotification.alertBody'),
                tag: alertEvent.alertId,
                url: '/chat',
                critico: alertEvent.priority === 'critical',
              })
              return
            }

            // FLUXO 06 · ELO 2 — preventive_due_card. The engine preventive-due
            // scanner (behind PREVENTIVE_SCHEDULER_ENABLED) pushes this to the
            // planner: "N preventive plan(s) due — generate the WO?". Tenant
            // guard first (the frame carries tenantId). Informational only —
            // the OS is born by human confirmation, never by this frame.
            if (msgType === 'preventive_due_card') {
              if (isCrossTenant(msg['tenantId'])) return
              usePreventiveDueStore.getState().setCard(msg as unknown as PreventiveDueCardEvent)
              return
            }

            // Multi-sheet import progress
            if (msgType === 'import_progress') {
              useImportStore.getState().updateImportProgress(msg as unknown as MultiSheetProgress)
              // Re-arm both timers — import is actively progressing.
              rearmarSeHaTurnoEmVoo(pendingMessageConvId)
              return
            }

            // Multi-sheet import complete
            if (msgType === 'import_complete') {
              const result = msg as unknown as MultiSheetImportResult
              const convId = chat.activeConversationId || ''
              chat.addMessage({
                id: `import-result-${Date.now()}`,
                conversationId: convId,
                content: JSON.stringify({ type: 'import_complete', data: result }),
                role: 'assistant',
                timestamp: Date.now(),
                status: 'sent',
              })
              useImportStore.getState().setStep('done')
              return
            }

            // Multi-sheet import error
            if (msgType === 'import_error') {
              const errMsg = (msg['error'] as string) || 'Import error'
              console.error('[ConnectionStore] Import error:', msg)
              set({ lastError: errMsg })
              useImportStore.getState().clearImport()
              return
            }

            if (msgType === 'error') {
              // Clear both timers on WS error so no late watchdog fires.
              clearTurnTimers()
              chat.setIsTyping(false)
              chat.clearProgress()
              const errMsg = (msg['message'] as string) || 'Erro desconhecido'
              set({ lastError: errMsg })
              // JilvupBs — surface a WS-side engine error on the live indicator
              // too, so the reasoning surface never silently empties.
              chat.setStreamError(
                typeof msg['message'] === 'string' &&
                  (msg['message'] as string).startsWith('errors.')
                  ? (msg['message'] as string)
                  : 'errors.streamFailed',
              )
              console.error('[ConnectionStore] Engine error:', errMsg)
              return
            }

            if (msgType === 'memory:fact-saved') {
              toast.success(i18n.t('memory.updated'), { duration: 3000 })
              import('./memoryStore')
                .then(({ useMemoryStore }) => {
                  void useMemoryStore.getState().fetchFacts()
                })
                .catch(() => {
                  /* non-critical */
                })
              return
            }

            // ─── Busca do plano do fabricante em segundo plano ───────────────
            // O turno não segura mais a conversa: o plano provisório volta em
            // segundos e a busca do manual continua sozinha no motor. Estes três
            // frames são o único vínculo entre aquele trabalho e a tela.
            //
            // GUARDA DE INQUILINO PRIMEIRO, nos três, como em todo handler WS:
            // sem ela um frame de outro cliente montaria um aviso persistente
            // com o NOME DO EQUIPAMENTO DELE no canto desta tela. O store não
            // tem defesa própria — a defesa é esta linha (contrato §3.1).
            if (msgType === 'plan.research.progress') {
              if (isCrossTenant(msg['tenantId'])) return
              useResearchJobsStore
                .getState()
                .applyProgress(msg as unknown as PlanResearchProgressEvent)
              return
            }

            if (msgType === 'plan.research.ready') {
              if (isCrossTenant(msg['tenantId'])) return
              const readyEvent = msg as unknown as PlanResearchReadyEvent
              useResearchJobsStore.getState().applyReady(readyEvent)
              // STO-354/STO-359 — mesma regra do sino: fora da tela vira
              // banner do SO; com a tela visível o aviso in-app já basta.
              void avisarForaDaTela({
                titulo: i18n.t('osNotification.planReadyTitle'),
                corpo: i18n.t('osNotification.planReadyBody', { label: readyEvent.label }),
                tag: readyEvent.jobId,
                url: `/chat/${readyEvent.conversationId}`,
                critico: false,
              })
              return
            }

            if (msgType === 'plan.research.failed') {
              if (isCrossTenant(msg['tenantId'])) return
              useResearchJobsStore.getState().applyFailed(msg as unknown as PlanResearchFailedEvent)
              return
            }

            // Async task completion toasts — fires for import_complete,
            // import_job_completed, import_error, web_search_completed
            handleAsyncTaskEvent(msgType, msg)
          } catch (err) {
            // P1 Impeccable finding 3: this used to only console.error, so a frame
            // the front failed to process vanished and the chat silently stopped
            // updating. Signal the user that data was lost (distinct from the
            // "engine down" ConnectionStatus indicator — the socket is fine here).
            console.error('[ConnectionStore] Failed to process WS message:', err, data)
            notifyEngineMessageDropped()
          }
        },
        onError: (error: Error, context: string) => {
          set({ lastError: `${context}: ${error.message}` })
          console.error(`[ConnectionStore] erro em ${context}:`, error.message)
          // P1 Impeccable finding 3: connection-manager routes an unparseable/
          // garbled WS frame here with this context. It means the client dropped
          // a piece of engine data while the socket stayed open — signal the user.
          // Other contexts are transport errors owned by the status indicator.
          if (context === 'ws_message_dropped') {
            notifyEngineMessageDropped()
          }
        },
      },
    )

    set({ manager })
    manager.connect(tenantId, userId)
  },

  sendMessage: async (content: string, conversationId?: string) => {
    const { manager } = get()
    if (!manager) throw new Error('Conexão não inicializada')
    return manager.send(content, conversationId)
  },

  sendRaw: (
    conversationId: string,
    content: string,
    messageId: string,
    options?: SendRawOptions,
    retry = false,
  ): boolean => {
    if (!canSendChatAction(options)) return false
    const {
      fileRefs,
      clickedActionLabel,
      inlineAttachments,
      commandSource,
      confirmationToken,
      intentId,
      onboardingAction,
      equipmentRegistrationAction,
      webSearchRetry,
      preventivePlanAction,
      orderExecutionAction: originalOrderExecutionAction,
      procedureAuthoringAction,
      npsAction,
      importFlowAction,
      command,
      closeNote,
      closingNoteWandAction,
      correctiveAction,
      notificationAction,
      planComparisonAction,
      photoPolicyAction,
      agentAction: originalAgentAction,
    } = options ?? {}
    // Snapshot once: the wire and the parked gate must authorize the same payload.
    const orderExecutionAction = cloneChatAction(originalOrderExecutionAction)
    const agentAction = cloneChatAction(originalAgentAction)
    // Novo-9 (2026-07-25) — o tenant que SAI vem da fonte autoritativa, lida
    // agora. `get().tenantId` é um retrato tirado no `initialize()`, antes de o
    // `/auth/status` responder, e por isso carregava o placeholder `user.id` do
    // AuthGuard em TODA requisição da rodada de QA. O `authStore` já aprende o
    // tenant certo; era só esta cópia que nunca aprendia.
    const { tenantId: connectionTenantId, userId } = get()
    const tenantId = resolveOutboundTenantId(useAuthStore.getState().tenantId, connectionTenantId)
    if (!tenantId || !userId) return false
    const authorizeFetch = captureChatSendAuthorization(options, tenantId, userId)

    // Estado REAL do aparelho, não o enum do próprio armazém.
    //
    // Até aqui nenhuma linha deste caminho perguntava se havia rede: as duas
    // guardas acima olham só dados nossos (inquilino, usuário). Um turno
    // disparado sem rede está condenado no primeiro instante, e mesmo assim
    // acendia o "pensando", armava os três temporizadores e devolvia "saiu" —
    // a mentira que deixa a fileira de botões travada esperando um desfecho
    // que não vem.
    //
    // ANTES de armar, como as duas guardas vizinhas: quem pode reprovar
    // reprova antes de acender. Assim o chamador recebe `false` na mesma
    // batida, mostra o aviso e a fileira volta a aceitar toque.
    //
    // LIMITE CONHECIDO, dito com todas as letras: isto NÃO cobre a suspensão
    // de aba do iPadOS — suspender não é trocar de rede, e `onLine` continua
    // verdadeiro. Aquele caso é a Frente 2 (voltar do segundo plano e prazo em
    // relógio de parede) e não é resolvido aqui.
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      console.warn('[ConnectionStore] sendRaw abortado — aparelho sem rede')
      return false
    }

    // Card-149 idempotency: every send carries a clientRequestId. If the
    // caller already passed a UUID-shaped messageId we use it as the key
    // (so component-level retries reuse the same id); otherwise we mint
    // one. The local dedup catches double-tap and remount-driven re-sends
    // before the network call. The backend dedup (Redis, see chat-stream
    // route) catches retries that survive the page reload window.
    const clientRequestId = isUuidShape(messageId) ? messageId : generateClientRequestId()
    const explicitReplay =
      retry && getLastTurnRequest(conversationId)?.clientRequestId === clientRequestId
    if (!registerClientRequestId(clientRequestId) && !explicitReplay) {
      // This id was sent within the last hour — drop the duplicate
      // silently, just like server-side idempotency hits do.
      console.warn('[ConnectionStore] sendRaw skipped — duplicate clientRequestId', clientRequestId)
      return false
    }
    // Cadastro local (spec 2026-10-24 §2.4): só o compositor espera o sync do
    // rascunho. Qualquer outro envio desta conversa (cartão, ação rápida, voz,
    // o clique do cadastro que caiu no servidor) descarta o rascunho antes — o
    // motor não o conhece, e o turno seria lido contra a etapa velha.
    descartarRascunhosPendentes(conversationId)

    // Unified transport (ADR-004 phase 1): every chat message — text-only,
    // with uploaded spreadsheet fileRefs, or with legacy inline attachments —
    // goes through the SSE endpoint. No WS-for-attachments branch, no
    // silent localStorage enqueue when WS is down. sendChatStream carries
    // fileRefs and inlineAttachments in the body so the engine can replay
    // the WS gateway's saveInlineAttachments enrichment.
    const replayRequest = recordLastTurnRequest(
      conversationId,
      content,
      {
        ...options,
        ...(orderExecutionAction ? { orderExecutionAction } : {}),
        ...(agentAction ? { agentAction } : {}),
      },
      clientRequestId,
    )
    const chat = useChatStore.getState()
    chat.setIsTyping(true)
    // O comando passou por TODAS as guardas acima (sem rede, duplicado, sem
    // tenant): é aceito. Quem rola a lista rearma o "seguir o fim" por aqui.
    chat.signalNewTurn(conversationId)
    marcaOnb('feedback')
    chat.clearProgress()
    // New turn — drop any stream error left over from a previous attempt so
    // the live reasoning indicator starts in the thinking state.
    chat.clearStreamError()
    chat.clearInactivityNotice()

    // Streaming bubble id — lazy-created on the first `chunk` SSE event so
    // we never insert an empty message when the engine path skips streaming
    // (tool-only turns, cached responses).
    let streamingMessageId: string | null = null
    // Last progress detail received before first chunk — carried into the
    // message richContent so the reasoning block can show "what the engine did".
    let pendingProcessingDetail: string | null = null
    /**
     * O card que o motor mandou ANTES do texto cru (evento `envelope`), à espera
     * do próximo progresso ou pedaço de texto para nascer JUNTO com a bolha.
     * Se a bolha já existe, `onEnvelope` aplica os cards imediatamente.
     */
    let pendingEnvelopeCards: Partial<RichContent> | null = null

    // Create a fresh controller for this stream; store it module-level so
    // abortCurrentStream() can reach it without selector overhead.
    currentAbortController = new AbortController()

    // Open this turn's epoch BEFORE any callback can fire, so stragglers from
    // the previous turn are already stale by the time they land.
    const turnId = beginTurn()

    // Sugestao e do TURNO. Deixar a do turno anterior ao lado da resposta nova
    // convida a pessoa a pedir de novo o que ela acabou de pedir.
    useSugestoesStore.getState().limpa()

    // Arm the idle watchdog and hard-cap timer for this turn.
    armIdleWatchdog(conversationId)
    startResponseTimer(conversationId)
    // …and the wall-clock deadline, which nothing re-arms. The two above are
    // silence detectors and a healthy keep-alive stream resets them forever;
    // this one is the guarantee that the turn ends.
    const resumableOptions = { ...options }
    delete resumableOptions.confirmationToken
    startTurnDeadline(conversationId, content, resumableOptions)

    sendChatStream(
      content,
      tenantId,
      userId,
      conversationId,
      {
        /**
         * Axis Insights — o painel abre DENTRO da conversa, no painel de
         * artifacts que ja existe. Nao ha tela nova nem rota nova (decisao da
         * torre): `useArtifactsStore.open` e o mesmo caminho que o plano de
         * manutencao e a citacao ja usam.
         *
         * O `spec` NAO e revalidado aqui de proposito: quem valida e o motor,
         * antes de gravar. Revalidar na tela criaria um segundo contrato para
         * divergir do primeiro em silencio. O que a tela protege e o RENDER —
         * cada bloco tem seu proprio limite de erro no `InsightsArtifact`.
         */
        onArtifactStart: (cabecalho) => {
          if (turnIsSettled(turnId)) return
          // Casca com esqueletos: os blocos chegam no `onArtifact`, na mesma caixa.
          openInsightsForConversation({
            id: cabecalho.id,
            title: cabecalho.title,
            type: 'insights',
            payload: { layout: { cols: 12 }, blocks: [] },
            source: { conversationId },
            version: cabecalho.version,
            streaming: true,
          })
        },
        onArtifact: (artifact) => {
          if (turnIsSettled(turnId)) return
          openInsightsForConversation({
            id: artifact.id,
            title: artifact.title,
            type: 'insights',
            // O cast E a fronteira de confianca, e ela e declarada: o motor
            // validou este spec com `parseDashboardSpec` ANTES de gravar. O que
            // a tela garante nao e o formato, e que um bloco que quebre ao
            // desenhar degrade so aquele bloco (limite de erro por bloco no
            // `InsightsArtifact`), nunca a conversa.
            payload: artifact.spec as DashboardSpec,
            source: { conversationId },
            version: artifact.version,
          })
          armTurnTimers(conversationId)
        },
        /**
         * Evento de painel fora de ordem ou spec ilegivel. O turno SEGUE: a
         * resposta em texto vale mesmo sem o grafico, e trocar a resposta da
         * pessoa por uma tela branca por causa de um painel seria pior do que
         * entregar a resposta sem ele.
         */
        onArtifactFalhou: (falha) => {
          console.warn('[insights] painel descartado:', falha.motivo, falha.id)
        },
        /**
         * SUGESTOES DO TURNO — os proximos passos que o motor propoe.
         *
         * Medido em 05/09/2026: `chat-stream.ts` ja tratava o evento
         * `suggestions` e chamava este callback, e uma varredura no repositorio
         * nao achou NENHUM consumidor. O motor mandava, o cano entregava, e a
         * tela jogava fora. Feature com zero consumidores e o mesmo defeito que
         * o tronco acabou de consertar no golden do Robson.
         */
        onSuggestions: (items) => {
          if (turnIsSettled(turnId)) return
          useSugestoesStore.getState().recebe(items)
        },
        /**
         * O CARD, ANTES DO TEXTO CRU (evento `envelope` do motor).
         *
         * Guarda para a próxima bolha e veste a já existente sem encerrá-la.
         * Só o `response` terminal atualiza os armazéns vivos e encerra o turno.
         */
        onEnvelope: (rich) => {
          if (turnIsSettled(turnId)) return
          const cards = pickRichContentCardChannels(rich)
          if ('webSearchResult' in cards) usePlanTasksPartialStore.getState().limpa(conversationId)
          if (Object.keys(cards).length > 0) {
            pendingEnvelopeCards = cards
            chat.mergeStreamingMessageCards(streamingMessageId, cards)
          }
          // Evento inbound = o cano está vivo; re-arma os dois vigias de silêncio,
          // pela mesma razão que `onProgress` e `onChunk` re-armam.
          armTurnTimers(conversationId)
        },
        onProcessing: () => {
          if (turnIsSettled(turnId)) return
          chat.setIsTyping(true)
          chat.clearProgress()
          // Engine started working again — clear any stale stream error.
          chat.clearStreamError()
          // Re-arm BOTH watchdogs: any inbound signal proves the pipe is alive.
          armTurnTimers(conversationId)
        },
        onProgress: (step, detail, data) => {
          if (turnIsSettled(turnId)) return
          if (step === PLAN_TASKS_PARTIAL_STEP) {
            // Grupo de tarefas do plano: NÃO vira passo de progresso — trocar o
            // `searching` derrubaria o painel de busca onde a tabela aparece.
            usePlanTasksPartialStore.getState().recebe(conversationId, data)
            armTurnTimers(conversationId, WEB_SEARCH_RESPONSE_TIMEOUT)
            return
          }
          chat.setProgress(step as Parameters<typeof chat.setProgress>[0], detail)
          pendingProcessingDetail = detail
          if (streamingMessageId === null) {
            // A BOLHA NASCE NO PROGRESSO, e nao so' no primeiro pedaco de texto.
            //
            // Medido no sandbox em 04/09 23:28, um turno de 130,5 s: a busca de
            // equipamento tentou AxiomK (36,4 s, "nao identificado"),
            // research-api (66,3 s, TIMEOUT) e so' entao o Tavily. O motor
            // emitiu `progress` esse tempo todo, o parser tratou e este
            // callback recebeu — e a tela ficou MUDA por dois minutos, porque
            // a bolha so' nascia no primeiro `onChunk` e chunk nenhum chegou
            // antes do terminal.
            //
            // Progresso emitido, transportado, recebido e jogado fora no ultimo
            // metro. 130 s com a tela viva e' irritante; 130 s de tela muda e'
            // indistinguivel de travado — e travado e' o que faz o mantenedor
            // desistir e ligar para o Denis.
            //
            // Nasce com o MESMO id e o MESMO formato que o `onChunk` usaria, de
            // proposito: quando o texto chegar, ele encontra a bolha pronta e
            // escreve dentro dela. Um caminho so', nao dois.
            streamingMessageId = `stream-${Date.now()}`
            useChatStore.getState().addMessage({
              id: streamingMessageId,
              conversationId: chat.activeConversationId || conversationId,
              content: '',
              role: 'assistant',
              timestamp: Date.now(),
              status: 'sending',
              isStreaming: true,
              richContent: { processing: detail, ...(pendingEnvelopeCards ?? {}) },
            })
          } else {
            useChatStore.getState().setStreamingMessageProcessing(streamingMessageId, detail)
          }
          // Re-arm BOTH watchdogs: a progress event = engine is alive, whatever
          // the step. Earlier fixes (#368 caps→180s, #369 re-arm on 'tools')
          // special-cased individual steps and still let the hard-cap fire on
          // the post-tool, content-silent window. The root rule is uniform: the
          // hard-cap measures pipe silence and is re-armed by any activity.
          // `searching` ganha a janela longa da pesquisa do plano (F4.5).
          armTurnTimers(
            conversationId,
            step === 'searching' ? WEB_SEARCH_RESPONSE_TIMEOUT : RESPONSE_TIMEOUT,
          )
        },
        onChunk: (chunk) => {
          if (!chunk) return
          if (turnIsSettled(turnId)) return
          // Re-arm BOTH watchdogs: a chunk = the answer is actively streaming.
          // It must extend the hard-cap, not only the idle watchdog — otherwise
          // a long final narration past 180s is cut mid-stream (aresta 1 residual).
          armTurnTimers(conversationId)
          if (streamingMessageId === null) {
            streamingMessageId = `stream-${Date.now()}`
            const convId = chat.activeConversationId || conversationId
            useChatStore.getState().addMessage({
              id: streamingMessageId,
              conversationId: convId,
              content: chunk,
              role: 'assistant',
              timestamp: Date.now(),
              status: 'sending',
              isStreaming: true,
              // A bolha NASCE VESTIDA quando o motor adiantou o card.
              richContent:
                pendingProcessingDetail || pendingEnvelopeCards
                  ? {
                      ...(pendingProcessingDetail ? { processing: pendingProcessingDetail } : {}),
                      ...(pendingEnvelopeCards ?? {}),
                    }
                  : undefined,
            })
            pendingProcessingDetail = null
            // The typewriter cursor takes over the affordance once tokens land.
            useChatStore.getState().setIsTyping(false)
            useChatStore.getState().clearProgress()
            return
          }
          useChatStore.getState().appendChunkToMessage(streamingMessageId, chunk)
        },
        onResponse: (data) => {
          marcaOnb('resposta')
          // O turno termina quando a resposta CHEGA, não quando ela é ACEITA.
          //
          // A guarda de inquilino abaixo ficava ANTES destas quatro linhas, e o
          // `return` dela pulava as quatro. Uma resposta recusada deixava o
          // "pensando" aceso e o turno SEM SELO, com dois estragos:
          //  1. o usuário esperava o vigia de silêncio (60s) e então recebia
          //     "a resposta demorou demais" — mensagem FALSA, porque a resposta
          //     chegou dentro do prazo e foi descartada aqui, de propósito;
          //  2. sem `settleCurrentTurn()`, qualquer evento atrasado do mesmo
          //     fluxo religava o indicador — a "porta lateral" que o comentário
          //     de `turnEpoch` neste próprio módulo descreve.
          //
          // Desfazer o estado do turno é responsabilidade da CHEGADA. O que a
          // guarda decide é só se o CONTEÚDO entra na conversa.
          settleCurrentTurn()
          // Clear both timers so they never fire late.
          clearTurnTimers()
          chat.setIsTyping(false)
          chat.clearProgress()
          if (isCrossTenant((data as Record<string, unknown>)['tenantId'])) {
            // A guarda acima já desliga o indicador da LISTA (isTyping/progress).
            // Mas se `chunk`/`progress` já tinham criado a bolha de streaming
            // desta mensagem (ex.: turno de equipe/OS que já vestiu texto antes
            // da resposta final ser recusada), essa bolha nunca passa por
            // `finalizeStreamingMessage` — só ele zera `isStreaming` — e o
            // `ProcessingIndicator` dentro dela (MessageBubble `isProcessingWhileStreaming`)
            // gira para sempre. Descarta a bolha: seu conteúdo já é de origem
            // rejeitada, não há o que preservar.
            if (streamingMessageId !== null) chat.removeMessage(streamingMessageId)
            return
          }
          const convId = chat.activeConversationId || data.conversationId || conversationId
          // Bug-fiação 2026-07-12 — the engine ALWAYS sends `suggestedActions`
          // (camelCase); `suggested_actions` (snake_case) was the only shape
          // read here since commit a28c6529 (2026-04-21) and is never sent by
          // the live engine, so every suggested-action from the backend was
          // silently dropped and MessageBubble fell back to the heuristic
          // `useFallbackActions` CTA instead. Prefer camelCase, tolerate snake.
          const rawSuggestedActions = data.suggestedActions ?? data.suggested_actions
          const sseSuggestedActions = Array.isArray(rawSuggestedActions)
            ? (rawSuggestedActions as SuggestedAction[])
            : undefined
          // Sprint 1A NEW1 — read actionCards from top-level SSE payload.
          // PR #210 patched only the WS branch above (now dead code per
          // ADR-004 phase 1 — chat unified transport to SSE). The live
          // code path is here; without this read, the engine's ACTION_CARDS
          // (judge + retry + fallback) never reach the UI even though they
          // are persisted in Message.metadata server-side.
          const sseActionCards = Array.isArray(data.actionCards)
            ? (data.actionCards as ActionCard[])
            : undefined
          // Fall back to inline <ACTION_CARDS> block parsing in case the
          // engine emits it inside content (legacy/unforeseen path).
          const parsedCards = parseActionCards(data.content, sseActionCards)
          const sseActionCardsFinal = parsedCards.cards ?? undefined
          const sseMessageMetadata = sseActionCardsFinal
            ? { actionCards: sseActionCardsFinal }
            : undefined
          // SDD-001 Phase 5c — merge tool-usage chips with the deterministic
          // import card envelope emitted by the engine's rich-content-mapper.
          // The card is what makes ImportProposalCard / ImportProgressBar
          // actually render; before this wiring the envelope was dropped on
          // the floor even though the engine produced it.
          // A linha de transparência prefere o RASTRO do motor (nome, parâmetro
          // redigido e contagem) e recua para a lista de nomes quando o motor é
          // mais velho que 0.71.0. Recuo é recuo: o card então diz "sem
          // parâmetros", que descreve o estado — nunca inventa parâmetro.
          const toolsUsed =
            data.toolTrace && data.toolTrace.length > 0
              ? data.toolTrace.map((p) => ({
                  name: p.name,
                  status: 'success' as const,
                  ...(p.params !== undefined ? { params: p.params } : {}),
                  ...(p.summary !== undefined ? { summary: p.summary } : {}),
                  ...(p.state !== undefined ? { state: p.state } : {}),
                }))
              : data.toolsUsed && data.toolsUsed.length > 0
                ? data.toolsUsed.map((name) => ({ name, status: 'success' as const }))
                : undefined
          const equipmentRegistrationFlow = data.richContent?.equipmentRegistrationFlow as
            | EquipmentRegistrationFlowSnapshot
            | undefined
          // Todos os canais de card saem de UMA lista declarada
          // (`RICH_CONTENT_CARD_KEYS`, no topo deste arquivo) em vez de uma
          // `const` por chave repetida em dois lugares. Ver o comentário de lá
          // para as três vezes em que uma chave esquecida virou card invisível.
          // Repassados verbatim: o chat nunca reescreve o que o motor mandou nem
          // recalcula um número.
          const cardChannels = pickRichContentCardChannels(
            data.richContent as Record<string, unknown> | undefined,
          )
          // Fase 2 — Camada A: o rascunho do preventivo também alimenta um store
          // vivo, para a tabela atualizar NO LUGAR em vez de correr com a lista
          // de mensagens.
          const preventivePlanDraft = data.richContent?.preventivePlanDraft as
            | PreventivePlanDraftRichContent
            | undefined
          if (equipmentRegistrationFlow) {
            useEquipmentRegistrationFlowStore.getState().applySnapshot(equipmentRegistrationFlow)
          }
          // Fatia 2 da máquina de importação — o snapshot do ImportFlow também
          // alimenta o store vivo, espelhando o equipmentRegistrationFlow acima.
          // O próprio store ignora envelope de recusa sem snapshot completo.
          const importFlowSnapshot = data.richContent?.importFlow
          if (importFlowSnapshot) {
            useImportFlowStore.getState().applySnapshot(importFlowSnapshot)
          }
          // FIX-3 (cluster "Salvar Rascunho") — seed/merge the live draft snapshot
          // BEFORE the message is built below, mirroring equipmentRegistrationFlow
          // above. PreventivePlanDraftTable's wrapper
          // reads the live rows from this store, so a stream of edit_row/add_row
          // turns updates ONE table in place instead of racing the message list.
          if (preventivePlanDraft) {
            usePreventivePlanDraftStore.getState().applySnapshot(preventivePlanDraft)
          }
          // Retrieve processing detail from the streaming message (set by onProgress)
          // so the finalized message preserves the "what the engine did" label.
          const existingProcessing =
            streamingMessageId !== null
              ? useChatStore.getState().messages.find((m) => m.id === streamingMessageId)
                  ?.richContent?.processing
              : undefined
          let richContent: RichContent | undefined
          const hasCardChannel = Object.keys(cardChannels).length > 0
          if (toolsUsed || existingProcessing || hasCardChannel) {
            richContent = {
              ...(toolsUsed ? { toolsUsed } : {}),
              ...(existingProcessing ? { processing: existingProcessing } : {}),
              ...cardChannels,
            }
          }
          if (
            parsedCards.content.trim() ||
            hasCardChannel ||
            sseActionCardsFinal?.length ||
            sseSuggestedActions?.length ||
            data.webSearch ||
            data.richContent?.voiceConfirmation
          ) {
            clearLastTurnRequest(conversationId, replayRequest)
            confirmComposerDraft(messageId)
            confirmAttachments(messageId)
          }
          if (streamingMessageId !== null) {
            // Promote the streaming bubble in place: same id so the user sees
            // a single message that finishes mid-render. We overwrite content
            // with the canonical payload (post-processed by the engine —
            // sanitization, tool blocks — that the streamed tokens lack).
            chat.finalizeStreamingMessage(streamingMessageId, {
              content: parsedCards.content,
              status: 'sent',
              richContent,
              webSearchMeta: data.webSearch ? (data.webSearch as WebSearchMeta) : undefined,
              suggestedActions: sseSuggestedActions,
              metadata: sseMessageMetadata,
            })
          } else {
            // One-shot finalResponse path (engine emitted `response` without
            // intermediate `chunk` events). Insert with `isStreaming: false`
            // so MessageBubble renders the canonical PacedMarkdown path —
            // PacedMarkdown owns its own typewriter cadence, so we still get
            // the reveal animation without leaving the bubble stuck waiting
            // for chunks that will never arrive. Previous version set
            // `isStreaming: true` which left the ResponseStream adapter
            // subscribed to a stream that never terminated and rendered
            // nothing (bug surfaced by the multi-turn conversational suite).
            // FIX-3 (cluster "Salvar Rascunho") — a preventivePlanDraft turn keys
            // the message on the STABLE draftId instead of Date.now(). The engine
            // re-emits the full draft on every edit_row/add_row/delete_row action
            // (action-only turn, no `chunk`, so streamingMessageId is always null
            // here), and chatStore.addMessage already dedups by exact id match
            // (see the sticky `errorBubbleId` pattern in the onError handler
            // below) — replacing the message in place instead of appending a new
            // bubble. Any other one-shot turn keeps the original Date.now() id
            // (still unique per turn; unrelated to this bug).
            const oneShotId = preventivePlanDraft
              ? `preventive-plan-draft-${preventivePlanDraft.draftId}`
              : `engine-${Date.now()}`
            chat.addMessage({
              id: oneShotId,
              conversationId: convId,
              content: parsedCards.content,
              role: 'assistant',
              timestamp: Date.now(),
              status: 'sent',
              isStreaming: false,
              richContent,
              webSearchMeta: data.webSearch ? (data.webSearch as WebSearchMeta) : undefined,
              suggestedActions: sseSuggestedActions,
              metadata: sseMessageMetadata,
            })
          }
          if (convId) {
            chat.updateConversation(convId, { updatedAt: Date.now() })
          }
          parkResponseConfirmation(data, { conversationId, request: replayRequest })
        },
        onError: (message) => {
          failComposerDraft(messageId, { tenantId, userId })
          settleCurrentTurn()
          chat.setIsTyping(false)
          chat.clearProgress()
          // STO-214 — para os temporizadores (nenhum precisa disparar depois
          // de um erro explícito de stream), mas NÃO apaga `turnInFlight`: o
          // fio morreu, mas o motor pode terminar o turno depois (medido:
          // socket morto 17:33:11, turno concluído 17:34:18). Manter o
          // rastro é o que permite a volta ao primeiro plano
          // (`darPorPerdidoSeAAbaFicouForaDemais`) confirmar com o servidor e
          // substituir a bolha de erro abaixo pela resposta real, em vez de
          // deixá-la presa aqui até a PRÓXIMA abertura do app.
          pararTemporizadoresDoTurno()
          // Bug A (QA Denis 2026-07-11/12, failures/2026-07-12-qa-denis-erro-
          // duplicado-e-cards-empilhados.md) — this handler ALWAYS also writes
          // a message-level error bubble below (finalizeStreamingMessage or
          // addMessage with status:'error'), which already renders its own
          // translated error text + retry CTA (MessageBubble). Setting
          // `streamError` here too used to make MessageList ALSO render the
          // standalone "reasoning-error" surface (AlertTriangle + Repetir) for
          // the exact same failure — the same sentence rendered twice with two
          // different retry buttons. The message bubble is the single source
          // of truth for this (SSE/live) path: it survives reload and IS the
          // conversation record. Clear any stale `streamError` instead (e.g.
          // left over from the WS `type: 'error'` branch above, which has no
          // message bubble of its own and is the ONE legitimate producer of
          // the standalone reasoning-error surface).
          chat.clearStreamError()
          // `chat-stream.sendChatStream` passes us i18n keys prefixed
          // `errors.` (e.g. `errors.networkBlocked`, `errors.streamFailed`,
          // `errors.authError`) so we can render localised copy. Legacy
          // call sites still pass raw text (HTTP error bodies); we treat
          // anything without the prefix as a literal display string.
          const isI18nKey = typeof message === 'string' && message.startsWith('errors.')
          const displayContent = isI18nKey
            ? i18n.t(message)
            : message || i18n.t('errors.streamFailed')
          if (streamingMessageId !== null) {
            // Preserve partial tokens already on screen; flag bubble errored.
            chat.finalizeStreamingMessage(streamingMessageId, {
              status: 'error',
              metadata: {
                ...useChatStore.getState().messages.find((m) => m.id === streamingMessageId)
                  ?.metadata,
                clientRequestId,
              },
            })
          } else {
            // Resolve the conversation id: the live store value wins over the
            // raw closure (seed / action-card sends pass '' here). An empty
            // conversationId leaked the bubble into every view via MessageList's
            // fallback filter — see MessageList.tsx.
            const convId = chat.activeConversationId || conversationId
            // Sticky per-turn id keyed on the resolved conversation. A re-send
            // into the same conversation reuses this id so addMessage REPLACES
            // the prior error bubble in place instead of stacking a duplicate.
            const errorBubbleId = `error-${convId || 'pending'}`
            chat.addMessage({
              id: errorBubbleId,
              conversationId: convId,
              content: displayContent,
              role: 'assistant',
              timestamp: Date.now(),
              status: 'error',
              metadata: { clientRequestId },
            })
          }
        },
        onKeepAlive: () => {
          if (turnIsSettled(turnId)) return
          // The engine is alive but content-silent (deterministic post-loop:
          // completion guard + critic + final-text generation). Re-arm both
          // watchdogs so this legitimate quiet window is not mistaken for a
          // wedged turn — the root fix for the post-tool silent-window race.
          armTurnTimers(conversationId)
        },
      },
      {
        authorizeFetch: () => (isCurrentTurn(turnId) ? authorizeFetch() : 'stale'),
        onStale: () => settleStaleCredentialTurn(turnId),
        fileRefs,
        inlineAttachments,
        clientRequestId,
        clickedActionLabel,
        // PR8.5 Gap A: only set for voice turns; undefined omits the field from the body.
        commandSource,
        // PR-E: single-use replay token; undefined omits the field from the body.
        confirmationToken,
        // Chip de indicador; undefined omite o campo do corpo.
        intentId,
        // Trilho finalização 2026-07-15 (SPEC §4.3, one channel): `onboardingAction`
        // carries BOTH the legacy card kind and the rail actionId+payload (plan-edit
        // confirm/cancel included). There is NO parallel `planEditAction`.
        onboardingAction,
        equipmentRegistrationAction,
        webSearchRetry,
        // Fase 2 — Camada A: deterministic preventive-plan builder action.
        preventivePlanAction,
        orderExecutionAction,
        procedureAuthoringAction,
        // B4 fatia 4b — resposta do pulse NPS. EXIGE o handler do motor no mesmo
        // deploy: sem ele a borda responde 400 ao `content: ''` (contrato §1.3).
        npsAction,
        // Fatia 2 da máquina de importação — clique do card do ImportFlow.
        // Mesma exigência do npsAction: o motor sintetiza a bolha na borda.
        importFlowAction,
        // Paleta de comandos (etapa 1, R10) — id do comando escolhido na
        // barra; `undefined` omite o campo do corpo para um turno normal.
        command,
        // CORR-B F3 — nota de encerramento; `undefined` omite o campo.
        closeNote,
        // CORR-B F4b (STO-721) — clique da varinha; `undefined` omite o campo.
        closingNoteWandAction,
        correctiveAction,
        notificationAction,
        planComparisonAction,
        photoPolicyAction,
        agentAction,
      },
      currentAbortController.signal,
    )

    return true // SSE request initiated
  },

  abortCurrentStream: () => {
    if (!currentAbortController) return
    // RN-3: clear every timer before aborting so no late watchdog fires, and
    // settle the turn so a callback already in flight cannot revive the spinner.
    clearTurnTimers()
    settleCurrentTurn()
    currentAbortController.abort()
    currentAbortController = null

    const chat = useChatStore.getState()
    chat.setIsTyping(false)
    chat.clearProgress()

    // Find any in-flight streaming bubble and mark it cancelled so the UI
    // can render it in a safe degraded state instead of leaving a dangling cursor.
    const streamingMsg = useChatStore
      .getState()
      .messages.find((m) => m.isStreaming === true && m.role === 'assistant')
    if (streamingMsg) {
      chat.finalizeStreamingMessage(streamingMsg.id, { status: 'cancelled' })
    }
  },

  sendFileUpload: (conversationId: string, fileId: string): boolean => {
    const { manager, userId, tenantId: connectionTenantId } = get()
    // Novo-9 — mesma regra do envio de mensagem: fonte autoritativa na hora.
    const tenantId = resolveOutboundTenantId(useAuthStore.getState().tenantId, connectionTenantId)
    if (!manager) return false

    // Card-149: clientRequestId for WS file_upload too. If the same fileId
    // arrives twice (auto-reconnect re-fire after WiFi blip), the engine
    // gateway dedup will reject the second one. We do NOT short-circuit
    // here because sendFileUpload is called once per UI commit and the
    // expected duplicate path is post-reconnect replay, not double-tap.
    const clientRequestId = generateClientRequestId()
    registerClientRequestId(clientRequestId)

    const payload = {
      type: 'file_upload',
      userId,
      tenantId,
      conversationId,
      locale: navigator.language || 'pt-BR',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      messageId: clientRequestId,
      clientRequestId,
      fileId,
    }

    const sent = manager.sendRaw(payload)
    if (!sent) {
      enqueue(payload as unknown as ReturnType<typeof buildOutboundMessage>)
    }
    return sent
  },

  disconnect: () => {
    clearLastTurnRequests()
    clearResponseTimer()
    const { manager } = get()
    if (manager) manager.destroy()
    set({ manager: null, state: 'DISCONNECTED', transport: 'offline' })
  },

  isConnected: () => get().state === 'CONNECTED',

  isUsable: () => {
    const s = get().state
    return s === 'CONNECTED' || s === 'DEGRADED'
    // AUTH_ERROR, OFFLINE, DISCONNECTED, CONNECTING, RECONNECTING are all not usable
  },

  statusText: () => {
    const { state, retryCount } = get()
    const map: Record<ConnectionState, string> = {
      DISCONNECTED: i18n.t('connection.status.disconnected'),
      CONNECTING: i18n.t('connection.status.connecting'),
      CONNECTED: i18n.t('connection.status.connected'),
      RECONNECTING: i18n.t('connection.status.reconnecting', {
        count: retryCount,
        max: 5,
      }),
      DEGRADED: i18n.t('connection.status.degraded'),
      OFFLINE: i18n.t('connection.status.offline'),
      AUTH_ERROR: i18n.t('errors.authError'),
    }
    return map[state]
  },

  statusColor: () => {
    const s = get().state
    // Severidade pela paleta industrial do projeto (ISA-101, src/design/tokens.css),
    // nunca pelo Tailwind cru. Três degraus, e só três — é a convenção de painel de
    // fábrica: normal / atenção / crítico. O que distingue CONNECTING de DEGRADED é o
    // TEXTO ao lado do ponto, não a cor; cor carrega gravidade, não identidade de estado.
    // Token (não hex) para que o mesmo estado acompanhe NIGHT e SHINE — o #10b981 antigo
    // era fixo e desbotava no tema claro.
    const map: Record<ConnectionState, string> = {
      DISCONNECTED: 'var(--axg-text-muted)',
      CONNECTING: 'var(--axg-severity-warn)',
      CONNECTED: 'var(--axg-accent)',
      RECONNECTING: 'var(--axg-severity-warn)',
      DEGRADED: 'var(--axg-severity-warn)',
      OFFLINE: 'var(--axg-severity-crit)',
      AUTH_ERROR: 'var(--axg-severity-crit)',
    }
    return map[s]
  },
}))
